import { useState } from 'react';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';

type Orientation = 'portrait' | 'landscape';

interface UseGeneratePDFOptions {
  fileName: string;
  orientation?: Orientation;
  avoidRowCuts?: boolean;
}

const useGeneratePDF = (
  templateRef: React.RefObject<HTMLDivElement>,
  { fileName, orientation = 'portrait', avoidRowCuts = false }: UseGeneratePDFOptions,
): { generate: () => Promise<void>; loading: boolean } => {
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!templateRef.current) return;
    setLoading(true);
    try {
      const element = templateRef.current;
      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      const pdf = new JsPDF({ orientation, unit: 'mm', format: 'a4' });
      const pageWidthMm = pdf.internal.pageSize.getWidth();
      const pageHeightMm = pdf.internal.pageSize.getHeight();
      const pxPerMm = canvas.width / pageWidthMm;
      const pageHeightPx = pageHeightMm * pxPerMm;

      if (avoidRowCuts) {
        const scale = canvas.height / element.offsetHeight;
        const rootRect = element.getBoundingClientRect();
        const rows = Array.from(element.querySelectorAll('tr'));
        const rowEdges = rows
          .map((r) => {
            const rect = (r as HTMLElement).getBoundingClientRect();
            return (rect.top - rootRect.top) * scale;
          })
          .filter((y) => y > 0)
          .sort((a, b) => a - b);

        const findRowEdge = (low: number, high: number): number | undefined =>
          rowEdges.filter((y) => y > low && y <= high).pop();

        const cuts: number[] = [0];
        let current = 0;
        while (current < canvas.height) {
          const limit = current + pageHeightPx;
          if (limit >= canvas.height) break;
          const next = findRowEdge(current, limit) ?? limit;
          cuts.push(next);
          current = next;
        }

        for (let i = 0; i < cuts.length; i += 1) {
          const sliceTop = cuts[i];
          const sliceBottom = i + 1 < cuts.length ? cuts[i + 1] : canvas.height;
          const sliceHeight = sliceBottom - sliceTop;

          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvas.width;
          pageCanvas.height = sliceHeight;
          const ctx = pageCanvas.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
            ctx.drawImage(canvas, 0, sliceTop, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
          }
          const imgData = pageCanvas.toDataURL('image/png');
          const sliceHeightMm = sliceHeight / pxPerMm;
          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, 0, pageWidthMm, sliceHeightMm);
        }
      } else {
        const imgHeight = (canvas.height * pageWidthMm) / canvas.width;
        let yOffset = 0;
        while (yOffset < imgHeight) {
          if (yOffset > 0) pdf.addPage();
          pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, -yOffset, pageWidthMm, imgHeight);
          yOffset += pageHeightMm;
        }
      }
      pdf.save(fileName);
    } catch (err) {
      toast.error('PDF generation failed');
    } finally {
      setLoading(false);
    }
  };

  return { generate, loading };
};

export default useGeneratePDF;
